/*
 * The fleet's sidebar content (`tiketo-cms` @ `c4387239`,
 * `frontend-app/src/components/layout`). Everything below the component's own
 * head — `SidebarItemComponent`, the collapsible one, the groups — is the
 * fleet's file unchanged.
 *
 * The head is where this console cannot follow, and it is a matter of what
 * exists rather than of taste: the fleet reads its items from
 * `useDynamicSidebar()` and then asks two of its own modules
 * (`modules-user-management`, `modules-organizations`) who the person is and
 * whether organisations are on. Neither module is here, and the second has no
 * counterpart at all. So the items arrive already chosen, from the one place
 * that knows both the role and the address to put in `$workspaceId` — see
 * `lib/sidebar-items.ts`.
 */
import { Link, useMatches } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar'
import type { SidebarItem } from '@/lib/sidebar-items'
import { useMemo, type PropsWithChildren } from 'react'
import { TooltipContent } from '@/components/ui/tooltip'

export const LayoutSidebarContent = ({
  items,
  children,
}: PropsWithChildren<{ items: SidebarItem[] }>) => {
  return (
    <SidebarContent>
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            {items.map((item) => (
              <SidebarItemComponent key={item.key} item={item} />
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
      {children && (
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu>{children}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}
    </SidebarContent>
  )
}

const CollapsibleSidebarItem = ({ item }: { item: SidebarItem }) => {
  const matches = useMatches()

  const defaultOpen = useMemo(() => {
    return matches.some((match) => match.pathname.startsWith(item.url))
  }, [matches, item.url])

  return (
    <Collapsible asChild className="group/collapsible" defaultOpen={defaultOpen}>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title ? <TooltipContent>{item.title}</TooltipContent> : undefined}
          >
            {item.icon}
            <span className="text-sm">{item.title}</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.children.map((child) => (
              <Link to={child.url} key={child.url} activeOptions={{ exact: true }}>
                {({ isActive }: { isActive: boolean }) => (
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      isActive={isActive}
                      className="data-[active=true]:bg-primary data-[active=true]:text-primary-foreground"
                    >
                      <span className="font-inherit">{child.title}</span>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                )}
              </Link>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

const SidebarItemComponent = ({ item }: { item: SidebarItem }) => {
  if (item.children.length > 0) {
    return <CollapsibleSidebarItem item={item} />
  }

  return (
    <Link to={item.url} key={item.url}>
      {({ isActive }: { isActive: boolean }) => (
        <SidebarMenuItem key={item.key}>
          <SidebarMenuButton 
            asChild 
            isActive={isActive}
            className="data-[active=true]:bg-primary data-[active=true]:text-primary-foreground"
          >
            <span>
              {item.icon}
              <span>{item.title}</span>
            </span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )}
    </Link>
  )
}
